/**
 * Goal Game scratch panel.
 *
 * The canvas logic is COPIED from `ScratchReveal` in pages/DemoVoucher.jsx on
 * purpose: that function is local to a page the Gift Generator depends on, and
 * this module is additive-only. Nothing is imported from or exported into it.
 *
 * The brand mark is a WATERMARK over the foil, at the ORG's own saved
 * scratch-surface logo, size (% of the shorter side) and opacity — the same
 * settings the Gift Generator uses. No brand is hardcoded here.
 */
import { useEffect, useRef } from 'react';

const LABEL_BAND = 34;

export const GoalScratch = ({ onComplete, label, logoUrl, logoOpacity, logoSize }) => {
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const started = useRef(false);
    const done = useRef(false);
    // The parent re-renders every 2 s (countdown + poll). Holding the callback
    // in a ref keeps it OUT of the effect's deps, so the foil is painted ONCE
    // and a Fan's scratch progress is never wiped mid-scratch.
    const completeRef = useRef(onComplete);
    completeRef.current = onComplete;

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width;
        canvas.height = rect.height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });

        const streaks = (alpha) => {
            ctx.strokeStyle = `rgba(255,255,255,${alpha})`;
            ctx.lineWidth = 1;
            for (let i = -canvas.height; i < canvas.width; i += 7) {
                ctx.beginPath();
                ctx.moveTo(i, 0);
                ctx.lineTo(i + canvas.height, canvas.height);
                ctx.stroke();
            }
        };

        const drawLabel = (centred) => {
            ctx.fillStyle = 'rgba(30,27,50,0.55)';
            ctx.font = 'bold 16px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText((label || 'Scratch here').toUpperCase(),
                         canvas.width / 2,
                         centred ? canvas.height / 2 : canvas.height - 14);
        };

        const paintSurface = (img) => {
            ctx.globalCompositeOperation = 'source-over';
            ctx.globalAlpha = 1;
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            const grad = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
            grad.addColorStop(0, '#eef0f5');
            grad.addColorStop(0.25, '#c9cdd7');
            grad.addColorStop(0.5, '#9aa0ac');
            grad.addColorStop(0.75, '#c9cdd7');
            grad.addColorStop(1, '#8b909c');
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            streaks(0.22);
            if (img) {
                const pct = Math.max(25, Math.min(100, Number(logoSize) || 38));
                const maxH = Math.min(Math.min(canvas.width, canvas.height) * pct / 100,
                                      canvas.height - LABEL_BAND - 6);
                const maxW = canvas.width * Math.min(1, (pct / 100) * 1.3);
                const scale = Math.min(maxW / img.width, maxH / img.height, 1);
                const w = img.width * scale;
                const h = img.height * scale;
                ctx.globalAlpha = Math.max(15, Math.min(60, Number(logoOpacity) || 30)) / 100;
                ctx.drawImage(img, (canvas.width - w) / 2, (canvas.height - LABEL_BAND - h) / 2, w, h);
                ctx.globalAlpha = 1;
                streaks(0.14);  // foil still reads as foil THROUGH the mark
            }
            drawLabel(!img);
            ctx.globalCompositeOperation = 'destination-out';
            ctx.fillStyle = 'rgba(0,0,0,1)';
            ctx.strokeStyle = 'rgba(0,0,0,1)';
            ctx.lineWidth = 46;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
        };

        // Plain foil synchronously — the panel is never transparent.
        paintSurface(null);
        if (logoUrl) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => { if (!started.current && !done.current) paintSurface(img); };
            img.src = logoUrl;
        }

        const pos = (e) => {
            const r = canvas.getBoundingClientRect();
            const t = e.touches && e.touches.length
                ? e.touches[0]
                : (e.changedTouches && e.changedTouches.length ? e.changedTouches[0] : e);
            return { x: t.clientX - r.left, y: t.clientY - r.top };
        };

        const check = () => {
            if (done.current) return;
            const img = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
            let clear = 0, total = 0;
            for (let i = 3; i < img.length; i += 40) { total++; if (img[i] === 0) clear++; }
            if (total && clear / total >= 0.6) { done.current = true; completeRef.current(); }
        };

        let last = null;
        const paint = (p) => {
            if (last) {
                ctx.beginPath();
                ctx.moveTo(last.x, last.y);
                ctx.lineTo(p.x, p.y);
                ctx.stroke();
            }
            ctx.beginPath();
            ctx.arc(p.x, p.y, 23, 0, Math.PI * 2);
            ctx.fill();
            last = p;
        };

        let sinceCheck = 0;
        const scratch = (e) => {
            if (!drawing.current || done.current) return;
            paint(pos(e));
            e.preventDefault();
            if (++sinceCheck >= 3) { sinceCheck = 0; check(); }
        };
        const start = (e) => { drawing.current = true; started.current = true; last = pos(e); paint(pos(e)); };
        const end = () => { drawing.current = false; last = null; check(); };

        canvas.addEventListener('mousedown', start);
        canvas.addEventListener('mousemove', scratch);
        window.addEventListener('mouseup', end);
        canvas.addEventListener('touchstart', start, { passive: false });
        canvas.addEventListener('touchmove', scratch, { passive: false });
        canvas.addEventListener('touchend', end);
        return () => {
            canvas.removeEventListener('mousedown', start);
            canvas.removeEventListener('mousemove', scratch);
            window.removeEventListener('mouseup', end);
            canvas.removeEventListener('touchstart', start);
            canvas.removeEventListener('touchmove', scratch);
            canvas.removeEventListener('touchend', end);
        };
    }, [label, logoUrl, logoOpacity, logoSize]);

    return (
        <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full cursor-pointer touch-none z-20"
            data-testid="goal-scratch-canvas"
        />
    );
};

export default GoalScratch;
